import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Download, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const COLS = ['email', 'role', 'planning', 'starting', 'budget', 'value', 'source', 'campaign', 'date', 'unsubscribed', 'email_sent'];
const LABELS = { email_sent: 'Email sent' };
const fmtDate = (v) => (v ? new Date(v).toLocaleString('en-GB') : '');

const toCsv = (rows) => {
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines = rows.map((m) => COLS.map((c) => esc(c === 'unsubscribed' ? (m[c] ? 'yes' : 'no') : m[c])).join(','));
  return [COLS.map((c) => LABELS[c] || c).join(','), ...lines].join('\n');
};

const StatCard = ({ label, children, testid }) => (
  <div className="card p-4" data-testid={testid}>
    <div className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--color-muted)' }}>{label}</div>
    {children}
  </div>
);

const Breakdown = ({ data }) => (
  <div className="space-y-1 text-sm">
    {Object.entries(data || {}).map(([k, v]) => (
      <div key={k} className="flex justify-between"><span>{k}</span><span className="font-semibold tabular-nums">{v}</span></div>
    ))}
    {!Object.keys(data || {}).length && <div style={{ color: 'var(--color-muted)' }}>No data yet</div>}
  </div>
);

export function FoundingMembersAdmin({ token }) {
  const [data, setData] = useState(null);
  const [role, setRole] = useState('');
  const [planning, setPlanning] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_URL}/api/admin/founding-members`, {
        params: { role, planning }, headers: token ? { Authorization: `Bearer ${token}` } : {}, withCredentials: true,
      });
      setData(res.data);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to load founding members');
    } finally {
      setLoading(false);
    }
  }, [token, role, planning]);

  useEffect(() => { load(); }, [load]);

  const members = useMemo(() => data?.members || [], [data]);
  const exportCsv = () => {
    const url = URL.createObjectURL(new Blob([toCsv(members)], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `founding_members_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const stats = data?.stats;
  return (
    <div data-testid="founding-members-admin">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Total sign-ups" testid="fm-stat-total">
          <div className="text-3xl font-bold" style={{ color: 'var(--color-navy)' }}>{stats?.total ?? '—'}<span className="text-base font-medium" style={{ color: 'var(--color-muted)' }}> / {stats?.target ?? 100}</span></div>
          <div className="h-2 rounded-full mt-3 overflow-hidden" style={{ background: 'var(--color-border)' }} data-testid="fm-target-progress">
            <div className="h-full rounded-full" style={{ width: `${Math.min(100, ((stats?.total || 0) * 100) / (stats?.target || 100))}%`, background: 'var(--color-navy)' }} />
          </div>
          <div className="text-xs mt-1" style={{ color: 'var(--color-muted)' }} data-testid="fm-target-pct">{Math.round(((stats?.total || 0) * 100) / (stats?.target || 100))}% of target</div>
        </StatCard>
        <StatCard label="Completed step 2" testid="fm-stat-step2"><div className="text-3xl font-bold" style={{ color: 'var(--color-navy)' }}>{stats ? `${stats.step2_pct}%` : '—'}</div></StatCard>
        <StatCard label="By role" testid="fm-stat-role"><Breakdown data={stats?.by_role} /></StatCard>
        <StatCard label="By source" testid="fm-stat-source"><Breakdown data={stats?.by_source} /></StatCard>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select className="input-styled" value={role} onChange={(e) => setRole(e.target.value)} data-testid="fm-filter-role">
          <option value="">All roles</option>
          {(data?.options?.roles || []).concat('Not given').map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <select className="input-styled" value={planning} onChange={(e) => setPlanning(e.target.value)} data-testid="fm-filter-planning">
          <option value="">All planning</option>
          {(data?.options?.planning || []).concat('Not given').map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <span className="text-sm" style={{ color: 'var(--color-muted)' }} data-testid="fm-filtered-count">{members.length} shown</span>
        <div className="flex-1" />
        <button className="btn-secondary flex items-center gap-2" onClick={load} disabled={loading} data-testid="fm-refresh-btn">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
        <button className="btn-secondary flex items-center gap-2" onClick={exportCsv} disabled={!members.length} data-testid="fm-export-csv-btn">
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      {error && <div className="p-3 mb-4 rounded-lg text-sm" style={{ background: '#FEE2E2', color: '#B91C1C' }}>{error}</div>}

      <div className="card overflow-x-auto">
        <table className="w-full text-xs" data-testid="fm-table">
          <thead>
            <tr style={{ background: 'var(--color-bg)' }}>
              {COLS.map((c) => <th key={c} className="text-left px-2 py-2 font-semibold capitalize whitespace-nowrap" style={{ color: 'var(--color-muted)' }}>{LABELS[c] || c}</th>)}
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.id} className="border-t" style={{ borderColor: 'var(--color-border)' }} data-testid={`fm-row-${m.email}`}>
                {COLS.map((c) => (
                  <td key={c} className="px-2 py-2 whitespace-nowrap">
                    {c === 'date' || c === 'email_sent' ? (fmtDate(m[c]) || '—') : c === 'unsubscribed' ? (m[c] ? 'Yes' : 'No') : (m[c] || '—')}
                  </td>
                ))}
              </tr>
            ))}
            {!members.length && (
              <tr><td colSpan={COLS.length} className="px-3 py-8 text-center" style={{ color: 'var(--color-muted)' }}>No founding members match these filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
